import { ecId, ecLean, handBack, insertRoll, itemField, record, upper, definitionNotHeld, type JanusCtx, type Row } from './context.js';
import { nextSpellPlan, type NextSpellPlan } from '../sanguine/next-spell.js';
import { planStoredStatusRemoval, removeStoredStatus } from './statuses/status-effect-removal-helpers.js';
import { janusDispatchRollTypes } from './rolls/janus-dispatch-roll-types.js';
import { contestingRoll } from './rolls/contesting-roll.js';
import { applyStatusEffects } from './statuses/apply-status-effects.js';
import { applyStatusEffect } from './statuses/apply-status-effect.js';
import { layerListEntryUuid } from '../actions/spell-components.js';
import { posCoord } from '../../shared/array-types.js';
import type { JanusInput } from './janus.js';

/** Freeze removal proofs before armor/counterspell dice. Newly armed rows never join an in-flight cast. */
export function preflightNextSpell(ctx: JanusCtx, ability: Row, caster: Row): void {
  if (ctx.trigger) return;
  for (const { status, layers } of nextSpellPlan(ability)?.carriers ?? []) {
    if (!upper(status.status_id)) handBack(`next-spell-unported:${layers[0]}:status-identity`);
    planStoredStatusRemoval(ctx, caster, status, 'single');
  }
}

/** Only successful spell settlement consumes carriers; counters, failed armor checks and non-spells keep them. */
export function settleNextSpell(ctx: JanusCtx, ability: Row, caster: Row): void {
  if (ctx.trigger) return;
  for (const { status, definition } of nextSpellPlan(ability)?.carriers ?? []) {
    if (ctx.statusesRemoved.some(removed => upper(removed.status_id) === upper(status.status_id))) continue;
    removeStoredStatus(ctx, caster, status, 'single');
    insertRoll(ctx, { roll_type_1: 'sanguine_spell_modified', roll_type_2: String(itemField(definition, 'item_name') ?? 'Sanguine'),
      roll_item_id: String(itemField(ability, 'item_id') ?? ''), by: caster, to: caster });
  }
}

/** Called only after the spell actually passes its per-recipient effect gate (a missed attack never queues a rider). */
export function queueNextSpellTarget(ability: Row, target: Row): void {
  const plan = nextSpellPlan(ability), id = ecId(target);
  if (plan && id && !plan.targets.includes(id)) plan.targets.push(id);
}

/** The roll dispatch of a prepared cast — a spell's (templateSpellRoll) or a volley projectile's (templateAbilitiesRoll, on the plan copy
 *  prepareNextSpellProjectile hands it); no plan = the plain dispatch. Overwhelming replays it; the per-recipient payloads follow. */
export function dispatchNextSpell(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, targets: Row[]): void {
  const plan = nextSpellPlan(ability);
  janusDispatchRollTypes(ctx, input, ability, caster, targets);
  if (!plan) return;
  // A volley parent's plan replays each PROJECTILE (its copy carries `duplicate`), never the container's own dispatch at the caster.
  if (plan.duplicate && !plan.volleyParent) janusDispatchRollTypes(ctx, input, ability, caster, targets);
  // A volley parent's payloads are delivered once after every projectile, to the creatures the projectiles reached (ability-use.ts); the
  // container's own dispatch reaches only the caster.
  if (plan.volleyParent) return;
  dispatchNextSpellPayloads(ctx, input, plan, caster);
}

/** The carriers' per-recipient payloads on `plan.targets` — the creatures the cast's effect gate passed (queueNextSpellTarget), each once:
 *  Encouraging's opportunity offer, Fearsome's contest rider, Umbral's following zone. Shared by the single cast (right after its dispatch)
 *  and the volley (after its last projectile, on the union of the projectiles' recipients). */
export function dispatchNextSpellPayloads(ctx: JanusCtx, input: JanusInput, plan: Pick<NextSpellPlan, 'carriers' | 'targets'>, caster: Row): void {
  const recipients = plan.targets.map(id => ctx.members.get(id)).filter((target): target is Row => !!target
    && Number(ecLean(target, 'encounter_creature_dead') ?? ctx.read.deadState(target)) === 0);
  for (const carrier of plan.carriers) {
    const definition = carrier.definition;
    if (carrier.layers.includes('next_spell_target_opportunity_attack'))
      for (const target of recipients) if (Number(ecLean(target, 'reaction')) > 0)
        (ctx.nextSpellOpportunityOffers ??= new Set()).add(ecId(target));
    if (carrier.layers.includes('next_spell_target_rider') && recipients.length) {
      const rider = { ...definition, item_layer_types: { ...record(definition.item_layer_types), roll_type: 'AD80D31D-898B-66CE-0894-1335D11B1529' } };
      const riderInput = { ...input, abilityId: upper(itemField(definition, 'item_id')), useAbilityVariable1: null, useAbilityVariable2: null };
      const priorConnection = ctx.statusConnectedId;
      ctx.statusConnectedId = null;
      try {
        contestingRoll(ctx, riderInput, rider, caster, recipients, target => {
          applyStatusEffects(ctx, riderInput, rider, 'status_effect_applied_to_target_after_contesting_roll_failed', caster, target);
          return true;
        });
      } finally { ctx.statusConnectedId = priorConnection; }
    }
    if (carrier.layers.includes('next_spell_paoe_applied_to_targets')) {
      const id = upper(layerListEntryUuid(ctx.read.value(definition, 'next_spell_paoe_applied_to_targets')));
      const zone = ctx.read.definition(id);
      if (!zone) definitionNotHeld('next-spell-unported:next_spell_paoe_applied_to_targets:definition-not-held', id);
      for (const target of recipients) {
        insertRoll(ctx, { roll_type_1: 'no_roll', roll_type_2: 'ability', roll_item_id: id, by: caster, to: target });
        const before = ctx.statusesAdded.length;
        applyStatusEffect(ctx, { ...input, abilityId: id }, zone, id, 'status_effect_applied', caster, target,
          undefined, true, true, 0, true, id);
        if (ctx.statusesAdded.length === before) continue;
        const status = ctx.statusesAdded[ctx.statusesAdded.length - 1]!;
        status.status_aoe_source = 1;
        for (const [axis, suffix] of [['x_pos', 'x'], ['y_pos', 'y'], ['z_pos', 'z']] as const) {
          const position = posCoord(target, axis) ?? 0;
          status[`status_origin_point_${suffix}`] = position;
          status[`status_distance_point_${suffix}`] = position;
        }
      }
    }
  }
}
